import { motion } from "framer-motion";
import { MonitorPlay } from "lucide-react";
import { Overline, OrnamentDivider, GoldDivider, CornerOrnaments } from "@/components/GoldDivider";
import CtaSection from "@/components/CtaSection";
import PhotoGrid from "@/components/PhotoGrid";
import usePhotos from "@/hooks/usePhotos";
import { IMAGES, PROCESS_STEPS } from "@/data/content";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Process() {
  const { images, loading } = usePhotos("process");

  return (
    <div className="velvet-deep">
      <section className="pt-36 pb-16 px-6" data-testid="process-hero">
        <div className="max-w-5xl mx-auto">
          <Overline>Our Process</Overline>
          <OrnamentDivider className="mt-6 !justify-start" width={200} />
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-ivory mt-5 leading-[1.03]">
            You see it in 3D before we build a thing.
          </h1>
          <p className="text-[#A9B7CE] text-lg sm:text-xl mt-6 max-w-2xl leading-relaxed">
            Most shops hand you a flat sketch and hope for the best. We design your exact kitchen in full 3D CAD, so
            what you approve on screen is what gets installed in your home.
          </p>
        </div>
      </section>

      {/* CAD advantage */}
      <section className="px-6 pb-20" data-testid="cad-advantage">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="relative group overflow-hidden">
            <img src={IMAGES.blueprint} alt="Real 3D CAD cabinet design by West Ouachita" className="w-full h-[300px] lg:h-[440px] object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/70 via-navy-deep/10 to-transparent" />
            <div className="absolute inset-0 ring-1 ring-inset ring-gold/20" />
            <p className="absolute bottom-6 left-6 text-ivory tracking-luxe uppercase text-xs border-l-2 border-gold pl-3">The CAD Advantage</p>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={1}
            className="bg-navy-surface border border-gold/25 p-10 flex flex-col justify-center">
            <MonitorPlay className="h-10 w-10 text-gold" strokeWidth={1.2} />
            <h2 className="font-serif text-3xl text-ivory mt-5">No surprises. No guesswork.</h2>
            <p className="text-[#A9B7CE] mt-4 leading-relaxed">
              Every door, drawer, finish and hardware choice is modeled to the millimeter. You walk your kitchen
              virtually, make changes freely, and approve a design you can actually see, long before installation day.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 5-step process */}
      <section className="bg-ivory py-24 px-6" data-testid="process-steps">
        <div className="max-w-3xl mx-auto text-center mb-14">
          <Overline>What Actually Happens</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl font-light text-navy mt-4">Five steps, done right.</h2>
        </div>
        <div className="max-w-4xl mx-auto">
          {PROCESS_STEPS.map((step, i) => (
            <motion.div key={step.n} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
              className="flex gap-6 sm:gap-10 pb-10">
              <div className="flex flex-col items-center">
                <span className="font-serif text-4xl sm:text-5xl text-gold font-light leading-none">{step.n}</span>
                {i < PROCESS_STEPS.length - 1 && <span className="w-px flex-1 bg-gold/30 mt-3" />}
              </div>
              <div className="pb-2">
                <h3 className="font-serif text-2xl sm:text-3xl text-navy">{step.title}</h3>
                <p className="text-navy/70 mt-2 leading-relaxed max-w-xl">{step.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Uploadable process/shop photos */}
      <section className="bg-ivory pb-24 px-6" data-testid="process-photos">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <Overline>Inside The Shop</Overline>
            <h2 className="font-serif text-4xl font-light text-navy mt-4">Where it all comes together.</h2>
          </div>
          <PhotoGrid images={images} loading={loading} columns={3} placeholderCount={3} minSlots={3}
            placeholderNote="Add CAD screenshots or shop / machinery photos here from the admin dashboard (Process section)." />
        </div>
      </section>

      {/* Why we do it */}
      <section className="velvet py-24 px-6 relative overflow-hidden" data-testid="why-we-do-it">
        <CornerOrnaments size={140} opacity={0.45} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Overline>Why We Do It</Overline>
          <blockquote className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-ivory mt-6 leading-tight">
            "A kitchen is where a family actually lives. We would rather build one thing right than a hundred things fast."
          </blockquote>
          <GoldDivider className="mt-10 max-w-[140px] mx-auto" />
          <p className="text-[#A9B7CE] mt-6 tracking-wider uppercase text-sm">West Ouachita Cabinets</p>
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
